import { AnthropicService } from '../../service/AnthropicService';
import { GeminiService } from '../../service/GeminiService';
import { GroqService } from '../../service/GroqService';
import { OpenAiService } from '../../service/OpenAiService';
import { PerplexityService } from '../../service/PerplexityService';
import { ReplicateService } from '../../service/ReplicateService';
import { XAiService } from '../../service/XAiService';
import { replacePlaceholders } from '../../util/myFunctionsCoreDisplay';
import { AiTextGeneratorFormValues } from '../AiConfigs/constants';

interface TechnologySystem {
  system: string;
  models?: Array<{ model: string; isReasoning?: boolean }>;
}

function isAnthropicReasoningModel(
  technologies: TechnologySystem[] | undefined,
  model: string | undefined,
): boolean {
  if (!model) {
    return false;
  }
  if (model.toLowerCase().includes('thinking')) {
    return true;
  }
  const sys = technologies?.find((s) => s.system === 'anthropic');
  const row = sys?.models?.find((m) => m.model === model);
  return row?.isReasoning === true;
}

export const commandTextNodeResponses = async (
  e: AiTextGeneratorFormValues,
  schema: any,
  flowId: string,
  technologies?: TechnologySystem[],
) => {
  const prompt = {
    prompt: replacePlaceholders(e.prompt, schema),
    content: replacePlaceholders(e.content, schema),
    instruction: replacePlaceholders(e.instruction, schema),
    persona: replacePlaceholders(e.persona, schema),
  };

  let response = null;

  if (e.AiSystem === 'Groq') {
    response = await GroqService.search({
      ...prompt,
      seed: +e.groqSeed,
      model: e.groqModel,
      temperature: +e.groqTemperature,
      max_tokens: +e.groqMaxTokens,
      top_p: +e.groqTopP,
      stop: e?.stop ? e?.stop : null,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
    });
  }

  if (e.AiSystem === 'openSources') {
    //falcon-40b-instruct
    //mpt-30b-chat
    // mpt-7b-storywriter
    // llama-2-70b-chat
    const options = {
      ...prompt,
      max_length: +e.max_length,
      temperature: +e.temperature,
      top_p: +e.top_p,
      repetition_penalty: +e.repetition_penalty,
      length_penalty: +e.length_penalty,
      no_repeat_ngram_size: +e.no_repeat_ngram_size,
      seed: +e.seed,
      flowId,
    };

    if (e.openSourceModelName === 'falcon-40b-instruct') {
      response = await ReplicateService.falcon40bInstruct(options);
    }

    if (e.openSourceModelName === 'mpt-7b-storywriter') {
      response = await ReplicateService.mpt7bStorywriter(options);
    }

    if (e.openSourceModelName === 'mpt-30b-chat') {
      response = await ReplicateService.mpt30bChat({
        ...prompt,
        max_new_tokens: +e.max_new_tokens,
        flowId,
      });
    }

    if (e.openSourceModelName === 'llama-2-70b-chat') {
      response = await ReplicateService.llama70bChat({
        // system_prompt: e.persona,
        ...prompt,
        max_new_tokens: +e.max_new_tokens,
        min_new_tokens: +e.min_new_tokens,
        temperature: +e.temperature,
        top_p: +e.top_p,
        top_k: +e.topK,
        seed: +e.seed,
        flowId,
      });
    }

    if (e.openSourceModelName === 'code-llama-34b') {
      response = await ReplicateService.codeLlama34b({
        ...prompt,
        flowId,
      });
    }

    if (e.openSourceModelName === 'code-llama-34b-instruct') {
      response = await ReplicateService.codeLlama34bInstruct({
        ...prompt,
        flowId,
      });
    }
  }

  if (e.AiSystem === 'OpenAI') {
    response = await OpenAiService.login({
      ...prompt,
      model: e.aiModel,
      effort: e.aiEffort,
      fp: +e.aiFp,
      pp: +e.aiPp,
      tokens: +e.aiTokens,
      temperature: +e.aiTemperature,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
    });
  }

  if (e.AiSystem === 'perplexityAi') {
    const { data } = await PerplexityService.response({
      ...prompt,
      flowId,
      top_p: +e.perplexityTopP,
      top_k: +e.perplexityTopK,
      temperature: +e.perplexityTemperature,
      max_tokens: +e.perplexityTokens,
      model: e.perplexityType,
      search_context_size: e.search_context_size,
      autoToken: e.autoToken,
    });

    response = {
      data: {
        message: data?.message,
        logs: data?.citations,
        usedTokens: data?.usedTokens,
      },
    };
  }

  if (e.AiSystem === 'anthropicAi') {
    const pathname = 'claude-v4';
    const model = e.anthropicModel;
    const useReasoningStream = isAnthropicReasoningModel(technologies, model);

    if (useReasoningStream) {
      response = await AnthropicService.reasoningStream({
        ...prompt,
        model,
        max_tokens: +e.anthropicToken,
        temperature: +e.anthropicTemperature,
        topP: +e.anthropicTopP,
        topK: +e.anthropicTopK,
        flowId,
        multimodalEnabled: e.multimodalEnabled !== false,
        autoToken: e.autoToken,
      });
    } else {
      response = await AnthropicService.response(
        {
          ...prompt,
          model,
          max_tokens: +e.anthropicToken,
          temperature: +e.anthropicTemperature,
          topP: +e.anthropicTopP,
          topK: +e.anthropicTopK,
          flowId,
          multimodalEnabled: e.multimodalEnabled !== false,
          autoToken: e.autoToken,
        },
        pathname,
      );
    }
  }

  if (e.AiSystem === 'Gemini') {
    const { data } = await GeminiService.gemini({
      ...prompt,
      model: e.geminiModel,
      temperature: +e.geminiTemp,
      topP: +e.geminiTopP,
      topK: +e.geminiTopK,
      maxOutputTokens: +e.geminiMaxOutputTokens,
      flowId,
      multimodalEnabled: e.multimodalEnabled !== false,
      autoToken: e.autoToken,
    });

    response = {
      data: {
        message: data.message,
        logs: data.thinking,
        usedTokens: data.usedTokens,
      },
    };
  }



  if (e.AiSystem === 'XAi') {
    let pathname = 'grok-4';
    let model = e.xaiModel;

    response = await XAiService.getCompletion(
      {
        ...prompt,
        model,
        effort: e.aiEffort,
        max_tokens: +e.aiTokens,
        temperature: +e.aiTemperature,
        flowId,
        type: 'chat',
        stream: e.streamEnabled,
        multimodalEnabled: e.multimodalEnabled !== false,
        autoToken: e.autoToken,
      },
      pathname,
    );
  }

  return response as any;
};
